import posthog from "posthog-js";

import { getMode } from "@/modules/shared/utils/env-mode";

/**
 * PostHog Feature Flags Utility
 *
 * Handles feature flag evaluation across environments:
 * - Development: Uses defaults/overrides, logs to console
 * - Staging/Production: Evaluates flags via PostHog SDK
 *
 * For analytics functions (initPostHog, captureEvent, identifyUser, resetPostHog),
 * see @/modules/shared/utils/posthog.
 */

const isDevelopment = () => {
  const mode = getMode();
  return mode === "development" || mode === "test";
};

// Store for development mode feature flag overrides
const devFeatureFlags: Record<string, boolean | string> = {};

/**
 * Check if a feature flag is enabled
 *
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag is not found (default: false)
 * @returns boolean indicating if the flag is enabled
 *
 * @example
 * if (isFeatureEnabled("new-booking-flow")) {
 *   // Show new booking UI
 * }
 */
export function isFeatureEnabled(
  flagKey: string,
  defaultValue: boolean = false,
): boolean {
  if (isDevelopment()) {
    const override = devFeatureFlags[flagKey];
    const result = override !== undefined ? Boolean(override) : defaultValue;
    console.log(`[PostHog] Feature Flag "${flagKey}":`, result, "(dev mode)");
    return result;
  }

  const result = posthog.isFeatureEnabled(flagKey);
  return result ?? defaultValue;
}

/**
 * Get a feature flag value (for multivariate flags)
 *
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag is not found
 * @returns The flag value (string, boolean, or undefined)
 *
 * @example
 * const variant = getFeatureFlag("checkout-button-color", "blue");
 * // Returns "blue", "green", or "red" based on flag configuration
 */
export function getFeatureFlag<T extends string | boolean = string | boolean>(
  flagKey: string,
  defaultValue?: T,
): T | undefined {
  if (isDevelopment()) {
    const override = devFeatureFlags[flagKey];
    const result = override !== undefined ? (override as T) : defaultValue;
    console.log(`[PostHog] Feature Flag "${flagKey}":`, result, "(dev mode)");
    return result;
  }

  const result = posthog.getFeatureFlag(flagKey);
  return (result as T) ?? defaultValue;
}

/**
 * Get feature flag payload (additional data attached to a flag)
 *
 * @param flagKey - The feature flag key
 * @returns The payload object or undefined
 *
 * @example
 * const payload = getFeatureFlagPayload("pricing-experiment");
 * // Returns { price: 9.99, currency: "USD" } or undefined
 */
export function getFeatureFlagPayload(
  flagKey: string,
): Record<string, unknown> | undefined {
  if (isDevelopment()) {
    console.log(
      `[PostHog] Feature Flag Payload "${flagKey}": undefined (dev mode)`,
    );
    return undefined;
  }

  const result = posthog.getFeatureFlagResult(flagKey);
  return result?.payload as Record<string, unknown> | undefined;
}

/**
 * Reload feature flags from PostHog
 *
 * Useful when user properties change and you need fresh flag evaluations.
 *
 * @returns Promise that resolves when flags are reloaded
 */
export function reloadFeatureFlags(): Promise<void> {
  if (isDevelopment()) {
    console.log("[PostHog] Reload Feature Flags (dev mode - no-op)");
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    // The PostHog JS runtime accepts a callback, but the type definitions
    // don't declare the parameter. Cast to avoid TS2554.
    (posthog.reloadFeatureFlags as (cb: () => void) => void)(resolve);
  });
}

/**
 * Register a callback for when feature flags are loaded
 *
 * @param callback - Function to call when flags are ready
 *
 * @example
 * onFeatureFlagsLoaded(() => {
 *   console.log("Feature flags are ready!");
 * });
 */
export function onFeatureFlagsLoaded(callback: () => void): void {
  if (isDevelopment()) {
    // In development, flags are immediately "available"
    callback();
    return;
  }

  posthog.onFeatureFlags(callback);
}

const FLAG_LOAD_TIMEOUT_MS = 3000;

/**
 * Wait for PostHog feature flags to load.
 *
 * Resolves as soon as flags are available, or after `timeoutMs` if PostHog
 * is unreachable — callers should pass a sensible default to `isFeatureEnabled`
 * so a flag outage doesn't take down a flag-gated route.
 *
 * @param timeoutMs - Max wait before resolving even if flags haven't loaded
 */
export function awaitFeatureFlagsLoaded(
  timeoutMs: number = FLAG_LOAD_TIMEOUT_MS,
): Promise<void> {
  if (isDevelopment()) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      resolve();
    };

    const timeout = setTimeout(finish, timeoutMs);
    posthog.onFeatureFlags(finish);
  });
}

/**
 * Set a feature flag override for development/testing
 *
 * Only works in development mode. Useful for testing different flag states.
 *
 * @param flagKey - The feature flag key
 * @param value - The value to set
 *
 * @example
 * // In development, force a flag to be enabled
 * setDevFeatureFlag("new-feature", true);
 */
export function setDevFeatureFlag(
  flagKey: string,
  value: boolean | string,
): void {
  if (!isDevelopment()) {
    console.warn("[PostHog] setDevFeatureFlag only works in development mode");
    return;
  }

  devFeatureFlags[flagKey] = value;
  console.log(`[PostHog] Dev override set: "${flagKey}" = ${value}`);
}

/**
 * The development overrides set with `setDevFeatureFlag`, read-only. Always
 * empty outside development and test.
 */
export function getDevFeatureFlags(): Readonly<
  Record<string, boolean | string>
> {
  return devFeatureFlags;
}

/**
 * Clear all development feature flag overrides
 */
export function clearDevFeatureFlags(): void {
  if (!isDevelopment()) {
    return;
  }

  Object.keys(devFeatureFlags).forEach((key) => delete devFeatureFlags[key]);
  console.log("[PostHog] Dev overrides cleared");
}
